import { useEffect, useState } from "react";
import { keyvault } from "../storage/keyvault.js";
import { useSession } from "../state/session.js";
import { useCallStore } from "../state/call.js";
import { useLockedAlerts } from "../services/lockWatch.js";

export function Unlock() {
  const unlock = useSession((s) => s.unlockWithPassphrase);
  const unlockPasskey = useSession((s) => s.unlockWithPasskey);
  const signOut = useSession((s) => s.signOut);
  const error = useSession((s) => s.error);
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState<"passphrase" | "passkey" | undefined>();
  // Erasing the account is irreversible, so it sits out of the way as small
  // text and takes a deliberate second step — it used to be a full-width
  // button right under Unlock, and got pressed by accident.
  const [showErase, setShowErase] = useState(false);
  // What arrived while locked — never who or what, just that it's there.
  const lockedRinging = useLockedAlerts((s) => s.ringing);
  const waitingMessages = useLockedAlerts((s) => s.messages);
  // A call that was already ringing when the app locked (e.g. its
  // notification was tapped) keeps ringing here too.
  const callRinging = useCallStore((s) => s.phase === "incoming");
  const ringing = lockedRinging || callRinging;

  useEffect(() => {
    void keyvault.method().then(setMethod);
  }, []);

  async function submit() {
    setBusy(true);
    try {
      await unlock(passphrase);
    } finally {
      setBusy(false);
      setPassphrase("");
    }
  }

  return (
    <div className="center">
      <div className="card">
        {ringing && (
          <div className="locked-alert ringing" role="alert">
            <span className="locked-alert-icon">📞</span>
            <span>
              <strong>Incoming call</strong>
              <span>Unlock to answer</span>
            </span>
          </div>
        )}
        {waitingMessages > 0 && (
          <div className="locked-alert" role="status">
            <span className="locked-alert-icon">💬</span>
            <span>
              <strong>
                {waitingMessages} new message{waitingMessages === 1 ? "" : "s"}
              </strong>
              <span>Unlock to read</span>
            </span>
          </div>
        )}
        <h1>Unlock TrueWave</h1>
        {method === "passkey" ? (
          <>
            <p>Use your passkey to unlock this device.</p>
            <button className="primary" disabled={busy} onClick={() => void unlockPasskey()}>
              Unlock with passkey
            </button>
          </>
        ) : (
          <>
            <p>Enter the passphrase you set on this device.</p>
            <input
              type="password"
              autoFocus
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void submit()}
            />
            <button className="primary" disabled={busy || passphrase.length < 8} onClick={submit}>
              {busy ? "Unlocking…" : "Unlock"}
            </button>
          </>
        )}
        {error && <div className="error">{error}</div>}
      </div>

      {showErase ? (
        <div className="card erase-card">
          <strong>Erase this account?</strong>
          <p>
            Everything on this device goes: your account, your contacts and every message. There is no way back
            in without the passphrase, and nothing can be recovered afterwards.
          </p>
          <div className="erase-actions">
            <button onClick={() => setShowErase(false)}>Cancel</button>
            <button
              className="danger-btn"
              onClick={() => {
                if (confirm("Erase this account and everything on this device? This can't be undone.")) void signOut();
              }}
            >
              Erase everything
            </button>
          </div>
        </div>
      ) : (
        <button className="subtle-link" onClick={() => setShowErase(true)}>
          Forgot your passphrase?
        </button>
      )}
    </div>
  );
}
